import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDidUpdate, useStateInRef } from 'seagull';
import { useOverlayContext, useStore } from '~/contexts';
import { BLOCK_SILENT } from '~/history';
import { useNavigationBlocker } from '~/hooks/useNavigationBlocker';
import { selectors } from '~/store/state-selectors';
const ManagerContext = /*#__PURE__*/ createContext({
    registerNavigationBackBlocker () {
        throw new Error('`registerNavigationBackBlocker` called before context initialization');
    }
});
export function useModalCloseManager() {
    return useContext(ManagerContext);
}
function useInternalManager() {
    const [blockers, setBlockers] = useState([]);
    const [cycleDone, setCycleDone] = useState(false);
    const shouldBlockRef = useRef(true);
    const navigate = useNavigate();
    return useMemo(()=>({
            registerNavigationBackBlocker (blocker) {
                setBlockers(blockers.concat(blocker));
                return {
                    unregister () {
                        setBlockers(blockers.filter((fn)=>fn === blocker));
                    }
                };
            },
            shouldBlock () {
                if (blockers.length === 0) {
                    return false;
                }
                if (!cycleDone) {
                    shouldBlockRef.current = true;
                    Promise.all(blockers.map((blocker)=>blocker())).catch(()=>[
                            true
                        ]).then((values)=>{
                        setCycleDone(true);
                        shouldBlockRef.current = values.some((isBlock)=>isBlock);
                        if (!shouldBlockRef.current) {
                            setTimeout(()=>{
                                navigate(-1);
                            }, 0);
                        }
                    });
                }
                if (!shouldBlockRef.current) {
                    setCycleDone(false);
                }
                return shouldBlockRef.current;
            }
        }), [
        blockers,
        cycleDone
    ]);
}
export function ModalCloseManagerProvider(param) {
    let { children } = param;
    const { getOpenedModals, hideAllModals } = useOverlayContext();
    const internalManager = useInternalManager();
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const getInternalManager = useStateInRef(internalManager);
    const block = useNavigationBlocker();
    const value = useMemo(()=>{
        return {
            registerNavigationBackBlocker: (blocker)=>getInternalManager().registerNavigationBackBlocker(blocker)
        };
    }, []);
    useDidUpdate(()=>{
        if (!hasUserInfo) {
            hideAllModals();
        }
    }, [
        hasUserInfo
    ]);
    useEffect(()=>{
        const unblock = block((_location, action)=>{
            const modals = getOpenedModals();
            if (modals.length > 0 && action !== 'REPLACE') {
                if (action === 'POP' && getInternalManager().shouldBlock()) {
                    return BLOCK_SILENT;
                }
                hideAllModals();
            }
            return undefined;
        });
        return unblock;
    }, [
        block
    ]);
    return /*#__PURE__*/ _jsx(ManagerContext.Provider, {
        value: value,
        children: children
    });
}
